import { baseApi } from "../base-api"

// Define API response types
export interface Container {
  id: string
  secondaryId?: number
  serialNo: string
  date: string
  vendorId: string
  vesselId: string
  shippingLineId: string
  bookedById: string
  shipperId: string
  portId: string
  term: string
  descriptionId: string
  tmp: string
  containerTypeId: string
  nValue: string
  containerNos: string
  trp: string
  truck: string
  ptType: string
  status: string
  shipmentStatusId: string
  shipmentStatus: string
  pValue: string
  gdNo: string
  sValue: string
  dppValue: string
  consigneeId: string
  qty: number
  packages: string
  gwt: string
  cValue: string
  efStatus: string
  formENumber: string
  exam: boolean
  pict: number
  phytoStatus: boolean
  chamber: boolean
  terminalCharges: boolean
  transport: string
  agency: string
  phyto: string
  invoiceStatus: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface CreateContainerParams {
  serialNo: string
  date: string
  vendorId: string
  vesselId: string
  shippingLineId: string
  bookedById: string
  shipperId: string
  portId: string
  term: string
  descriptionId: string
  tmp: string
  containerTypeId: string
  nValue: string
  containerNos: string
  trp: string
  truck: string
  ptType: string
  status: string
  shipmentStatusId: string
  shipmentStatus: string
  pValue: string
  gdNo: string
  sValue: string
  dppValue: string
  consigneeId: string
  qty: number
  packages: string
  gwt: string
  cValue: string
  efStatus: string
  formENumber: string
  exam: boolean
  pict: number
  phytoStatus: boolean
  chamber: boolean
  terminalCharges: boolean
  transport: string
  agency: string
  phyto: string
  invoiceStatus: string
}

export interface CreateContainerResponse {
  data: Container
  meta: IPagination
  msg: string
  success: boolean
}

interface GetContainerParams {
  id: string
}

export interface GetContainerResponse {
  data: Container
  meta: IPagination
  msg: string
  success: boolean
}
export enum ContainerInvoiceStatus {
  DRAFT = "DRAFT",
  NOT_SEND = "NOT_SEND",
  SENT = "SENT",
  PAID = "PAID",
  CANCELLED = "CANCELLED"
}

interface GetContainersParams {
  page?: number
  limit?: number
  invoiceStatus?: ContainerInvoiceStatus
}

interface GetContainersResponse {
  data: Container[]
  meta: IPagination
  msg: string
  success: boolean
}

export interface UpdateContainerParams extends CreateContainerParams {
  id: string
}

export interface UpdateContainerResponse {
  data: Container | null
  meta: IPagination
  msg: string
  success: boolean
}

interface DeleteContainerParams {
  id: string
}

export interface PaymentContainer {
  id: string
  cnee: { id: string; name: string }
  containerNos: string
  gd: string
  term: string
  truck: string
}
interface GetPaymentContainersResponse {
  data: PaymentContainer[]
  meta: IPagination
  msg: string
  success: boolean
}

export interface GetPaymentContainersParams {
  page?: number
  limit?: number
  portId?: string
  containerTypeId?: string
  vendorId?: string
  descriptionId?: string
  invoiceStatus?: ContainerInvoiceStatus
}
interface DeleteContainerResponse {
  success: boolean
  message: string
}

// API slice for container module
export const containerApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // Get all containers with pagination and search
    getContainers: builder.query<GetContainersResponse, GetContainersParams>({
      query: params => {
        return {
          url: `containers`,
          method: "GET",
          params
        }
      },
      providesTags: ["Container"]
    }),

    // Get single container by ID
    getContainer: builder.query<GetContainerResponse, GetContainerParams>({
      query: id => ({
        url: `containers/${id}`,
        method: "GET"
      }),
      providesTags: ["Container"]
    }),

    // Create new container
    createContainer: builder.mutation<CreateContainerResponse, CreateContainerParams>({
      query: container => ({
        url: "containers",
        method: "POST",
        body: container
      }),
      invalidatesTags: ["Container"]
    }),

    // Update existing container
    updateContainer: builder.mutation<UpdateContainerResponse, UpdateContainerParams>({
      query: ({ id, ...container }) => ({
        url: `containers/${id}`,
        method: "PATCH",
        body: container
      }),
      invalidatesTags: ["Container"]
    }),

    // Delete container
    deleteContainer: builder.mutation<DeleteContainerResponse, DeleteContainerParams>({
      query: id => ({
        url: `containers/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Container"]
    }),
    getPaymentContainers: builder.query<GetPaymentContainersResponse, GetPaymentContainersParams>({
      query: params => ({
        url: `containers/payment/containers`,
        method: "GET",
        params
      })
    })
  })
})

// Export hooks for use in components
export const {
  useGetContainersQuery,
  useLazyGetContainersQuery,
  useGetContainerQuery,
  useCreateContainerMutation,
  useUpdateContainerMutation,
  useDeleteContainerMutation,
  useGetPaymentContainersQuery,
  useLazyGetPaymentContainersQuery
} = containerApi
